import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { TONE } from '../../data/constants'
import {
  RESTRICTED_GUEST_USER_ROLE_ID,
  STANDARD_GUEST_USER_ROLE_ID,
  USER_GUEST_USER_ROLE_ID,
  buildTenantAuthzRows,
  getUserSelfConsentPolicyAssignments,
  isPresent,
} from '../../data/tenantAuthzPolicy'
import Chip from '../../components/Chip/Chip'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SettingNameCell from '../../components/SettingNameCell/SettingNameCell'
import SubBlock from '../../components/SubBlock/SubBlock'

const UNAVAILABLE_VALUE = '-'
const DATA_NOT_ASSESSED_MESSAGE_ID = 'AI_RISK_REPORT.DATA_NOT_ASSESSED'
const USER_SELF_CONSENT_MESSAGE_ID = 'AI_RISK_REPORT.THIRD.AUTHZ.PERMISSION_GRANT.USER_SELF_CONSENT'
const PERMISSION_GRANT_DISABLED_MESSAGE_ID = 'AI_RISK_REPORT.THIRD.AUTHZ.PERMISSION_GRANT.DISABLED'

const GUEST_USER_ROLE_LABEL_BY_ID = {
  [USER_GUEST_USER_ROLE_ID]: 'Guest',
  [STANDARD_GUEST_USER_ROLE_ID]: 'GuestUser',
  [RESTRICTED_GUEST_USER_ROLE_ID]: 'RestrictedGuest',
}

const getPositiveCountTone = (hasValue, count, positiveTone = TONE.DANGER) => {
  if (!hasValue) return undefined
  return count > 0 ? positiveTone : TONE.OK
}

const getDeviationTone = (hasValue, deviates, deviationTone = TONE.DANGER) => {
  if (!hasValue) return undefined
  return deviates ? deviationTone : TONE.OK
}

const formatGuestUserRoleId = (value) => {
  if (!value) return value

  const roleId = String(value)
  const roleLabel = GUEST_USER_ROLE_LABEL_BY_ID[roleId.toLowerCase()]
  return roleLabel ? `${roleId} (${roleLabel})` : roleId
}

const renderAuthzSettingName = (row) => (
  <SettingNameCell
    labelId={row.labelId}
    sourceKey={row.sourceKey}
    tooltipElementId={`ai-risk-authz-setting-tip-${row.key}`}
    tooltipMessageId={row.tooltipId}
  />
)

const getPermissionGrantPolicyChipTone = (result) => {
  if (result === true) return TONE.OK
  if (result === false) return TONE.WARN
  return TONE.NEUTRAL
}

const renderPermissionGrantPolicyChips = (assignments, result) => (
  <div className="d-flex flex-wrap align-items-start gap-1">
    {assignments.map((assignment) => (
      <span key={assignment.raw} title={assignment.raw}>
        <Chip tone={getPermissionGrantPolicyChipTone(result)}>{assignment.label}</Chip>
      </span>
    ))}
  </div>
)

const renderPermissionGrantPolicyGroup = ({ title, children }) => (
  <div className="d-flex flex-column align-items-start gap-1">
    <div className="text-muted small fw-semibold">{title}</div>
    {children}
  </div>
)

const renderPermissionGrantPolicyCurrentValue = (row) => {
  if (!Array.isArray(row.current)) return UNAVAILABLE_VALUE

  const assignments = getUserSelfConsentPolicyAssignments(row.current)

  return renderPermissionGrantPolicyGroup({
    title: <FormattedMessage id={USER_SELF_CONSENT_MESSAGE_ID} />,
    children:
      assignments.length > 0 ? (
        renderPermissionGrantPolicyChips(assignments, row.result)
      ) : (
        <Chip tone={TONE.OK}>
          <FormattedMessage id={PERMISSION_GRANT_DISABLED_MESSAGE_ID} />
        </Chip>
      ),
  })
}

const renderAuthzCurrentValue = (row) => {
  if (!isPresent(row.current)) return UNAVAILABLE_VALUE
  if (row.key === 'permissionGrantPolicy') return renderPermissionGrantPolicyCurrentValue(row)

  const value = row.formatCurrent ? row.formatCurrent(row.current) : String(row.current)
  if (row.result === false && row.deviationTone === TONE.WARN)
    return <strong className="text-warning fw-bold">{value}</strong>
  if (row.result === false) return <strong className="text-danger fw-bold">{value}</strong>
  return value
}

const renderAuthzRecommendedValue = (row) => String(row.recommended)

const getCurrentValueFormatter = (row) => {
  if (row.key === 'guestRole') return formatGuestUserRoleId
  return undefined
}

const getAuthzCurrentSearchValues = (row, intl) => {
  if (!isPresent(row.current)) return []
  if (row.key !== 'permissionGrantPolicy') {
    const formattedValue = row.formatCurrent ? row.formatCurrent(row.current) : String(row.current)
    return [row.current, formattedValue]
  }

  const assignments = getUserSelfConsentPolicyAssignments(row.current)
  const groupLabel = intl.formatMessage({ id: USER_SELF_CONSENT_MESSAGE_ID })
  if (assignments.length === 0) {
    return [groupLabel, intl.formatMessage({ id: PERMISSION_GRANT_DISABLED_MESSAGE_ID })]
  }

  return [groupLabel, ...assignments.flatMap(({ raw, policyId, label }) => [raw, policyId, label])]
}

export default function TenantAuthz({ tenant, score, gap = null }) {
  const intl = useIntl()
  const authorizationPolicy = tenant?.authorizationPolicy
  const rows = buildTenantAuthzRows(tenant?.authorizationPolicyAssessment).map((row) => ({
    ...row,
    formatCurrent: getCurrentValueFormatter(row),
  }))
  const inviteSetting = authorizationPolicy?.allowInvitesFrom
  const inviteSettingAudited = isPresent(inviteSetting)
  const inviteSettingResult = rows.find(({ key }) => key === 'invites')?.result ?? null
  const assessedRows = rows.filter(({ result }) => result !== null)
  const deviationCount = assessedRows.filter(({ result }) => result === false).length
  const stats = [
    {
      key: 'auditedSettings',
      value: authorizationPolicy ? assessedRows.length : UNAVAILABLE_VALUE,
      label: <FormattedMessage id="AI_RISK_REPORT.THIRD.AUTHZ.STAT_TOTAL" />,
    },
    {
      key: 'deviations',
      value: authorizationPolicy ? deviationCount : UNAVAILABLE_VALUE,
      denom: authorizationPolicy ? assessedRows.length : undefined,
      tone: getPositiveCountTone(Boolean(authorizationPolicy), deviationCount),
      label: <FormattedMessage id="AI_RISK_REPORT.THIRD.AUTHZ.STAT_DEVIATE" />,
    },
    {
      key: 'invites',
      value: inviteSetting ?? UNAVAILABLE_VALUE,
      tone: getDeviationTone(inviteSettingAudited, inviteSettingResult === false, TONE.WARN),
      truncateValue: true,
      label: <FormattedMessage id="AI_RISK_REPORT.THIRD.AUTHZ.STAT_INVITES" />,
    },
  ]
  const columns = [
    {
      key: 'setting',
      header: <FormattedMessage id="AI_RISK_REPORT.SETTINGS.COL_SETTING" />,
      render: renderAuthzSettingName,
      sortable: true,
      sortValue: (row) => intl.formatMessage({ id: row.labelId }),
      searchValue: (row) => [intl.formatMessage({ id: row.labelId }), row.sourceKey],
    },
    {
      key: 'current',
      header: <FormattedMessage id="AI_RISK_REPORT.SETTINGS.COL_CURRENT" />,
      render: renderAuthzCurrentValue,
      sortable: true,
      sortValue: (row) => row.current,
      searchValue: (row) => getAuthzCurrentSearchValues(row, intl),
    },
    {
      key: 'recommended',
      header: <FormattedMessage id="AI_RISK_REPORT.SETTINGS.COL_RECOMMENDED" />,
      render: renderAuthzRecommendedValue,
      sortable: true,
      sortValue: (row) => row.recommended,
      searchValue: (row) => row.recommended,
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.THIRD.AUTHZ.TITLE"
      subtitleId="AI_RISK_REPORT.THIRD.AUTHZ.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      stats={stats}
    >
      <ReportTable
        columns={columns}
        rows={authorizationPolicy ? rows : []}
        affectedEntityLabelId="AI_RISK_REPORT.TABLE.ENTITY.SETTINGS"
        isAffectedEntityTable={false}
        empty={
          gap ? (
            <EmptyState messageId={DATA_NOT_ASSESSED_MESSAGE_ID} values={{ dataType: 'Tenant authorization policy' }} />
          ) : (
            <EmptyState messageId="AI_RISK_REPORT.NO_AUTHZ_POLICY" tone={TONE.OK} />
          )
        }
      />
    </SubBlock>
  )
}

TenantAuthz.propTypes = {
  tenant: PropTypes.shape({
    authorizationPolicy: PropTypes.shape({
      allowInvitesFrom: PropTypes.string,
      allowEmailVerifiedUsersToJoinOrganization: PropTypes.bool,
      allowedToSignUpEmailBasedSubscriptions: PropTypes.bool,
      allowUserConsentForRiskyApps: PropTypes.bool,
      guestUserRoleId: PropTypes.string,
      defaultUserRolePermissions: PropTypes.shape({
        allowedToCreateApps: PropTypes.bool,
        allowedToCreateTenants: PropTypes.bool,
        allowedToCreateSecurityGroups: PropTypes.bool,
        permissionGrantPoliciesAssigned: PropTypes.arrayOf(PropTypes.string),
      }),
    }),
    authorizationPolicyAssessment: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  gap: PropTypes.shape({ reason: PropTypes.string }),
}
